import {
  Tile,
  TileContent,
  TileGroup,
  TileText,
  Typography,
} from '@one-thd/sui-atomic-components';
import { Calendar } from '@one-thd/sui-icons';
import React, { useEffect } from 'react';
import { formatPickupDate } from '@src/utils/general';
import {
  MORE_DATES_VALUE,
  REQUEST_DIFFERENT_DATE_VALUE,
} from '@src/utils/calendar';
import { setShowCalendar, useReturnStore } from '@src/stores/returnStore';
import {
  setSelectedPickupDate,
  useSummaryStore,
} from '@src/stores/summaryStore';
import { IPickupDate } from '@src/models/api';

export const LORPPickupDateTiles = () => {
  const pickupDates = useReturnStore((state) => state.pickupDates);
  const selectedPickupDate = useSummaryStore(
    (state) => state.selectedPickupDate,
  );
  const showCalendar = useReturnStore((state) => state.showCalendar);

  const visibleSlots = pickupDates.slice(0, 5);

  useEffect(() => {
    if (!selectedPickupDate && pickupDates?.[0]?.date) {
      setSelectedPickupDate(pickupDates?.[0].date);
    }
  }, [selectedPickupDate, pickupDates]);

  const { shortFullDate, longFullDate } = selectedPickupDate
    ? formatPickupDate(selectedPickupDate)
    : { shortFullDate: '', longFullDate: '' };

  const getValue = (id?: string) => {
    if (showCalendar && id !== REQUEST_DIFFERENT_DATE_VALUE) {
      return MORE_DATES_VALUE;
    }
    return selectedPickupDate;
  };

  const getPickupDate = () => {
    const pickupDate = pickupDates[0]?.date;
    if (!pickupDate) {
      return { shortFullDate: '', longFullDate: '' };
    }

    if (selectedPickupDate === MORE_DATES_VALUE) {
      return {
        shortFullDate: formatPickupDate(pickupDate).shortFullDate || '',
        longFullDate: formatPickupDate(pickupDate).longFullDate || '',
      };
    }
    if (selectedPickupDate === REQUEST_DIFFERENT_DATE_VALUE) {
      return {
        shortFullDate: '',
        longFullDate: '',
      };
    }

    return { shortFullDate, longFullDate };
  };

  return (
    <div className="sui-mb-4">
      <Typography variant="body-lg">
        Pickup Scheduled for:{' '}
        <span className="sui-font-bold" data-testid="pickup-date">
          {getPickupDate().shortFullDate}
        </span>
      </Typography>
      <TileGroup
        value={getValue(selectedPickupDate)}
        onChange={(
          _event: React.ChangeEvent<HTMLInputElement>,
          date: string,
        ) => {
          // When there is no value change the id is null
          if (date === null) {
            return;
          }
          if (date === MORE_DATES_VALUE) {
            setSelectedPickupDate(MORE_DATES_VALUE);
            setShowCalendar(true);
          } else {
            setSelectedPickupDate(date);
            setShowCalendar(false);
          }
        }}
        aria-label="Pickup date options"
        className="sui-mt-4 sui-mb-1 sui-grid sui-grid-cols-3 sui-gap-2 sui-w-full"
      >
        {visibleSlots.map((slot: IPickupDate) => {
          const {
            prefix,
            dayOfWeek,
            monthDay,
            shortFullDate: shortDate,
          } = formatPickupDate(slot.date);
          return (
            <Tile key={slot.date} value={slot.date} aria-label={shortDate} grow>
              <TileContent orientation="vertical">
                {prefix ? (
                  <>
                    <TileText>{`${prefix}, `}</TileText>
                    <TileText>
                      {dayOfWeek} {monthDay}
                    </TileText>
                  </>
                ) : (
                  <>
                    <TileText>{dayOfWeek}</TileText>
                    <TileText>{monthDay}</TileText>
                  </>
                )}
              </TileContent>
            </Tile>
          );
        })}
        <Tile
          key={MORE_DATES_VALUE}
          value={MORE_DATES_VALUE}
          aria-label="more dates"
          grow
        >
          <TileContent orientation="vertical">
            <Calendar size="small" />
            <TileText>More Dates</TileText>
          </TileContent>
        </Tile>
      </TileGroup>
    </div>
  );
};
